import { ISelectionFormValues, TOptionVariant } from '../../utils/types';
import {
  IApproverProfile,
  ICostCodeWithSelections,
  ICreateSelectionItemBody,
  ISelectionItem,
  ISelectionVariant,
  ISelectionVariantBody,
  IUpdateSelectionItemBody,
  ReminderStatusEnum,
  SelectionStatusEnum,
} from 'redux-store/api/selections';
import { v1 as uuidV1 } from 'uuid';
import { ITag } from 'redux-store/types';
import { ensureHttpUrl, getSelectionDefaultUploads } from '../../utils/helpers';
import { toIsoUtcDate, utcDateToZoned } from 'utils/postDateHelper';

const getDefaultProfileIds = (profiles: IApproverProfile[]): ITag[] =>
  profiles.map(({ id, firstName, lastName }) => ({
    id,
    title: `${firstName} ${lastName}`,
  }));

export const getSelectionFormDefaultValues = (
  projectId: number,
  isEdit: boolean,
  selection?: ISelectionItem,
  selectionCostCode?: ICostCodeWithSelections,
  canCreateLibrarySelection?: boolean
): Partial<ISelectionFormValues> => {
  if (selection && isEdit) {
    const {
      title,
      room: { id: roomId },
      costCode: { id: costCodeId },
      status,
      remindersProfiles,
      projectPhaseItemId,
      projectPhaseId,
      endDate,
      options,
      reminderStatus,
    } = selection;

    const formatedEndDate = !!endDate ? utcDateToZoned(endDate as string) : null;

    return {
      projectId,
      title,
      costCodeId,
      roomId,

      status,
      reminderStatus,
      ...(endDate && { endDate: formatedEndDate }),
      ...(projectPhaseId && { projectPhaseId }),
      ...(projectPhaseItemId && { projectPhaseItemId }),
      profileIds: remindersProfiles ? getDefaultProfileIds(remindersProfiles) : [],

      options: options
        .map(
          (
            {
              id,
              orderNo,
              isSelected,
              title: optionTitle,
              amount,
              quantity,
              description,
              url,
              logo,
              uploads,
              variants,
            },
            index
          ) => ({
            id: `${id}`,
            orderNo,
            isSelected,
            title: optionTitle,
            amount: String(amount),
            quantity,
            variants: mapVariantsWithOrderNo(variants),
            uploads: getSelectionDefaultUploads(uploads),
            ...(logo && {
              logo: {
                id: logo.id,
                url: logo.url,
              },
            }),
            ...(description && { description }),
            ...(url && { url }),
          })
        )
        .sort((a, b) => a.orderNo - b.orderNo),
    };
  }

  return {
    projectId,
    ...(selectionCostCode?.id && { costCodeId: selectionCostCode.id }),
    status: SelectionStatusEnum.PENDING,
    reminderStatus: ReminderStatusEnum.UNASSIGNED,
    profileIds: [],
    saveToLibrary: canCreateLibrarySelection,
    options: [
      {
        variants: [],
        id: uuidV1(),
        orderNo: 0,
        uploads: [],
        quantity: 1,
        isSelected: false,
      },
    ],
  };
};

export const getCreateSelectionRequestBody = ({
  options,
  profileIds,
  reminderStatus,
  title: selectionTitle,
  ...rest
}: ISelectionFormValues): ICreateSelectionItemBody => {
  let restArgs = rest;
  const isCustom = reminderStatus === ReminderStatusEnum.CUSTOM;
  const isUnassigned = reminderStatus === ReminderStatusEnum.UNASSIGNED;

  if (isCustom || isUnassigned) {
    restArgs = {
      ...rest,
      projectPhaseId: null,
      projectPhaseItemId: null,
      ...(rest.endDate && { endDate: toIsoUtcDate(rest.endDate) }),
      ...(isUnassigned && { endDate: null }),
    };
  }

  return {
    ...restArgs,
    title: options.length > 1 ? selectionTitle : options[0].title,
    profileIds: profileIds.map(({ id }) => id),
    options: options.map(
      (
        { uploads, logo, amount, description, quantity, title, url, isSelected, variants, id },
        index
      ) => ({
        ...(!isNaN(Number(id)) && { id: Number(id) }),
        orderNo: index,
        uploads: uploads.map(({ id }) => id),
        logoId: logo?.id ?? null,
        title,
        amount: String(amount),
        quantity,
        variants: mapVariantsWithIndex(variants),
        isSelected:
          rest.status === SelectionStatusEnum.PENDING
            ? false
            : options.length > 1
              ? isSelected
              : true,
        description: description?.trim() || null,
        url: url ? ensureHttpUrl(url) : null,
      })
    ),
  };
};

export const getUpdateSelectionRequestBody = (
  formValues: ISelectionFormValues,
  selection: ISelectionItem
): IUpdateSelectionItemBody => {
  const oldSelectedOption = selection.options.find(({ isSelected }) => isSelected);

  const oldTotalAmount = oldSelectedOption
    ? oldSelectedOption.quantity * Number(oldSelectedOption.amount)
    : null;

  return {
    updatedData: getCreateSelectionRequestBody(formValues),
    selectionId: selection.id,
    newStatus: formValues.status,
    oldStatus: selection.status,
    oldCategoryId: selection.costCode.id,
    ...(oldTotalAmount && { oldTotalAmount }),
  };
};

export const mapVariantsWithIndex = (variants: TOptionVariant[]): ISelectionVariantBody[] =>
  variants?.map((variant) => ({
    name: variant.name,
    options: variant.options.map((option: any, i: number) => ({
      name: option.name,
      orderNo: i,
    })),
  })) ?? [];

export const mapVariantsWithOrderNo = (variants: ISelectionVariant[]): TOptionVariant[] =>
  variants?.map((variant) => ({
    name: variant.name,
    options: variant.options.map((option) => ({
      name: option.name,
      orderNo: option.orderNo,
    })),
  })) ?? [];
